<template>
    <div class="d-flex gap-4 flex-column">

        <StatsWidgets />

        <Card :header="$t('availabilityAnalysis')" overlay :loading="loading">

            <template #header>
                <div class="d-flex gap-2 align-items-center">
                    {{ $t('availabilityAnalysis') }}
                    <ButtonIcon
                        v-b-tooltip.hover.noninteractive.bottom
                        icon="download"
                        variant="light"
                        :icon-size="14"
                        class="p-1 rounded-max mb-auto width-25 height-25"
                        :title="$t('downloadTheEntireReport')"
                        :loading="generatingReport"
                        @click="generateAndNavigateToExport"
                    />
                </div>
            </template>

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <MultiselectAvailableCampaigns
                            :allow-empty="false"
                            :value="filters.campaigns"
                            :disabled="loading"
                            @changeTrackBy="filters.campaigns = $event"
                            @loading="setLoading('campaigns', $event)"
                        />
                        <Multiselect
                            v-show="filters.showProductGroupsResults"
                            :value="filters.groups"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="filters.groups = $event"
                            @loading="setLoading('groups', $event)"
                        />
                        <FormCategoryTreeSelect
                            v-show="!filters.showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="filters.categories"
                            :disabled="loading"
                            class="width-200"
                            preselect
                            prevent-empty-selection
                            :clearable="false"
                            @change="filters.categories = $event"
                            @loading="setLoading('categories', $event)"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox
                            v-model="filters.showProductGroupsResults"
                            switch
                            :disabled="loading"
                        >
                            {{ $t('showResultsForProductGroups') }}
                        </b-form-checkbox>
                    </div>
                </div>
            </template>

            <ErrorMessage :value="requestData.getError()" />

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex gap-4 flex-column">

                <OffersInTime />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AverageAvailabilityOfProductsByStores />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AverageAvailabilityByProducts />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AvailabilityInTimeAccordingToStores />

            </div>

        </Card>

    </div>
</template>



<script>
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import { usePerSpaceFilters } from '@/mixins/usePerSpaceFilters';
    import dataExportService from '@/services/dataExportService';
    import StatsWidgets from './StatsWidgets.vue';
    import OffersInTime from './OffersInTime.vue';
    import AverageAvailabilityOfProductsByStores from './AverageAvailabilityOfProductsByStores.vue';
    import AverageAvailabilityByProducts from './AverageAvailabilityByProducts.vue';
    import AvailabilityInTimeAccordingToStores from './AvailabilityInTimeAccordingToStores.vue';

    const DEFAULT_FILTERS = {
        showProductGroupsResults: false,
        campaigns: [],
        groups: [],
        categories: [],
    };

    export default {
        name: 'AvailabilityAnalysis',
        mixins: [
            usePerSpaceFilters(DEFAULT_FILTERS, {
                filtersPath: 'filters',
                loadingStatesPath: 'loadingStates',
                spaceIdPath: 'spaceId',
                apiParamsPath: 'apiParams',
                loadHandlerName: 'fetchData',
                abortHandlerName: 'abortRequest',
                resetHandlerName: 'resetData',
            }),
        ],
        components: {
            Multiselect,
            FormCategoryTreeSelect,
            StatsWidgets,
            OffersInTime,
            AverageAvailabilityOfProductsByStores,
            AverageAvailabilityByProducts,
            AvailabilityInTimeAccordingToStores,
            MultiselectAvailableCampaigns,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestData: this.$req('space/AvailabilityCharts'),
                requestDataCompare: this.$req('space/AvailabilityCharts'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                requestGroupProducts: this.$req('producerProductGroups/groupProducts'),
                filters: structuredClone(DEFAULT_FILTERS),
                loadingStates: {
                    campaigns: false,
                    groups: false,
                    categories: false,
                },
                generatingReport: false,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent?.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.filters.campaigns?.length) requestParams.campaigns = [...this.filters.campaigns].sort().join(',');
                if (!this.filters.showProductGroupsResults && this.filters.categories?.length) requestParams.categories = [...this.filters.categories].sort().join(',');
                if (this.filters.showProductGroupsResults && this.filters.groups?.length) requestParams.groups = [...this.filters.groups].sort().join(',');
                return { urlParams, requestParams };
            },
            data() {
                return this.requestData.getLoading() ? {} : this.requestData.getData() || {};
            },
            loadingData() {
                return this.requestData.getLoading();
            },
        },
        methods: {
            abortRequest() {
                this.requestData.abortAll();
                this.requestDataCompare.abortAll();
            },
            resetData() {
                this.requestData.resetAll();
                this.requestDataCompare.resetAll();
            },
            async fetchData() {
                const { urlParams, requestParams } = this.apiParams;
                const mainPromise = this.requestData.get({ urlParams, requestParams });
                const comparePromise = this.requestDataCompare.get({
                    urlParams,
                    requestParams: {
                        ...requestParams,
                        startDate: this.timeRange.compareStartDate,
                        endDate: this.timeRange.compareEndDate,
                    },
                });
                await mainPromise;
                await comparePromise;
                return mainPromise;
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            resetFilters() {
                this.resetPerSpaceDefaults();
                this.resetAllLoading();
            },
            async getSelectedGroupsProductsIds() {
                const productIdsPromises = this.filters.groups.map(async groupId => {
                    try {
                        const response = await this.requestGroupProducts.id(`group-${groupId}`).get({ urlParams: { groupId } });
                        return Object.keys(response.items || {});
                    } catch (error) {
                        console.error(`Failed to fetch products for group ${groupId}:`, error);
                        return [];
                    }
                });

                const productIdsArrays = await Promise.all(productIdsPromises);
                return [...new Set(productIdsArrays.flat())];
            },
            async generateAndNavigateToExport() {
                const hasCategories = this.filters.categories?.length > 0;
                const hasGroups = this.filters.groups?.length > 0 && this.filters.showProductGroupsResults;

                if (!hasCategories && !hasGroups) {
                    this.$toastr.warning(this.$t('dataExport.pleaseSelectFilters'));
                    return;
                }

                this.generatingReport = true;
                try {
                    const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange || {};
                    const campaigns = this.filters.campaigns?.length ? [...this.filters.campaigns] : null;
                    const categories = !this.filters.showProductGroupsResults && this.filters.categories?.length ? [...this.filters.categories] : null;
                    const groups = this.filters.showProductGroupsResults && this.filters.groups?.length ? [...this.filters.groups] : null;
                    const products = this.filters.showProductGroupsResults && this.filters.groups?.length ? await this.getSelectedGroupsProductsIds() : null;

                    await dataExportService.postExportProductsAvailabilityReports({
                        urlParams: { spaceId: this.spaceId },
                        requestParams: {
                            startDate,
                            endDate,
                            compareStartDate,
                            compareEndDate,
                            campaigns,
                            categories,
                            groups,
                            products,
                        },
                    });
                    this.$toastr.info(this.$t('dataExport.generationFeedback'));
                    this.$router.push({ name: 'DataExport' });
                } catch (error) {
                    this.$toastr.error(error.message, this.$t('errorOccurred'));
                } finally {
                    this.generatingReport = false;
                }
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
